import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Camera, LogOut, Loader2, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBiens, useCreateBien } from "@/hooks/useBiens";
import { closeGate } from "./TempPublishLoginPage";
import { cn } from "@/lib/utils";
import { today } from "@/lib/today";
import { QualityBadge } from "@/components/ui/QualityBadge";
import { PortalsCellMini } from "@/components/biens/PortalsCellMini";
import type { BienRow } from "@/schemas/bien";
import type { Bien } from "@/types/bien";

/**
 * Liste du module /publi-temp. Filtre les biens créés via cette interface
 * (origin = 'publi_temp') et propose :
 *  - "+ Créer un bien" (vide, sans validation)
 *  - Click sur une ligne -> fiche bien (où le bouton Publier vit)
 *
 * Colonnes alignees V1 /suivi-publication : ref / projet / commune /
 * statut bien / photos / vidéo / textes / modif texte / statut publication
 * / portails. Pas de sidebar.
 */

const STATUT_DISPO_LABEL: Record<string, string> = {
  "DISPONIBLE": "Disponible",
  "EN PRÉPARATION": "En préparation",
  "LOUÉ": "Loué",
  "VENDU": "Vendu",
};

/** alerteTexte < today => modifier texte requis (badge rouge). */
function shouldModifyText(alerteTexte: string | null | undefined): boolean {
  if (!alerteTexte) return false;
  const d = new Date(alerteTexte);
  if (isNaN(d.getTime())) return false;
  return d < today();
}

/** ISO YYYY-MM-DD -> DD/MM/YY. */
function formatAlerteDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y.slice(-2)}`;
}

/** OkBadge — Textes valides (textesOk === true) vs neutre. Aligne V1. */
function OkBadge({ ok }: { ok: boolean }) {
  if (!ok) return <span className="text-text-muted">—</span>;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-success/10 text-success">
      OK
    </span>
  );
}

export function TempPublishListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [search, setSearch] = useState("");

  const { data: biens, isLoading } = useBiens("admin");

  const myBiens = useMemo(() => {
    const list = (biens ?? []) as BienRow[];
    return list
      .filter((b) => b.origin === "publi_temp")
      .sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""));
  }, [biens]);

  const filtered = useMemo(() => {
    if (!search.trim()) return myBiens;
    const q = search.toLowerCase();
    return myBiens.filter((b) =>
      [b.referenceInterne, b.reference, b.adresse, b.ville, b.projet]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [myBiens, search]);

  const createBien = useCreateBien("admin");

  const handleLogout = async () => {
    closeGate();
    await signOut();
    navigate("/publi-temp/login", { replace: true });
  };

  const handleCreate = () => {
    const id = crypto.randomUUID();
    // BienSchema attend des string non-null pour certaines colonnes texte —
    // on initialise a '' pour eviter qu un seul ZodError fasse echouer le
    // parse de TOUTE la liste au prochain refetch.
    const initial = {
      id,
      origin: "publi_temp",
      referenceInterne: "",
      projet: "",
      adresse: "",
      ville: "",
      propertyBox: "",
    } as unknown as Parameters<typeof createBien.mutate>[0];
    createBien.mutate(initial, {
      onSuccess: (created) => {
        if (created?.id) navigate(`/publi-temp/${created.id}`);
      },
      onError: (err) => {
        alert(`Erreur création : ${err instanceof Error ? err.message : String(err)}`);
      },
    });
  };

  const th = "text-left px-4 py-3 text-[10px] font-semibold tracking-wider uppercase text-text-muted";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-surface border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera size={18} className="text-accent" />
          <h1 className="text-base font-semibold text-text-primary">
            Publication Skyline Renting
          </h1>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary cursor-pointer transition-colors"
        >
          <LogOut size={13} />
          Déconnexion
        </button>
      </header>

      <main className="flex-1 px-6 py-6 max-w-7xl w-full mx-auto">
        <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-semibold text-text-primary">Mes biens</h2>
            <p className="text-sm text-text-muted mt-1">
              Créez vos biens et publiez-les sur le site web.
            </p>
          </div>
          <button
            type="button"
            onClick={handleCreate}
            disabled={createBien.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-accent text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {createBien.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Plus size={14} />
            )}
            Créer un bien
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (référence, adresse, projet, commune)…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-border bg-surface outline-none"
          />
        </div>

        {/* Table — colonnes alignees V1 /suivi-publication */}
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50 divide-x divide-border">
                <th className={th}>Référence du bien</th>
                <th className={th}>Projet</th>
                <th className={th}>Commune</th>
                <th className={th}>Statut bien</th>
                <th className={cn(th, "text-center")}>Photos</th>
                <th className={cn(th, "text-center")}>Vidéo</th>
                <th className={cn(th, "text-center")}>Textes</th>
                <th className={th}>Modifier texte</th>
                <th className={th}>Statut publication</th>
                <th className={cn(th, "text-center")}>Portails</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center">
                    <Loader2 className="h-5 w-5 animate-spin text-accent inline-block" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-sm text-text-muted">
                    {myBiens.length === 0
                      ? "Aucun bien encore créé. Cliquez sur \"Créer un bien\" pour commencer."
                      : "Aucun résultat pour cette recherche."}
                  </td>
                </tr>
              ) : (
                filtered.map((b) => <TempPublishRow key={b.id} bien={b} t={t} />)
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

/**
 * Ligne — clic = ouvre la fiche en édition. Pas d action inline pour rester
 * aligne V1 (la publication explicite se fait dans la fiche bouton "Publier").
 */
function TempPublishRow({ bien, t }: { bien: BienRow; t: (k: string) => string }) {
  const navigate = useNavigate();

  const dispo = (bien.disponibilite || "").toUpperCase();
  const dispoLabel = STATUT_DISPO_LABEL[dispo] || "—";

  const td = "px-4 py-3 text-xs text-text-secondary";

  return (
    <tr
      onClick={() => navigate(`/publi-temp/${bien.id}`)}
      className="border-b border-border last:border-b-0 hover:bg-background/50 cursor-pointer transition-colors divide-x divide-border"
    >
      <td className={cn(td, "font-mono text-text-primary")}>
        {bien.referenceInterne || bien.reference || bien.id.slice(0, 8)}
      </td>
      <td className={td}>
        {bien.projet || <span className="text-text-muted">—</span>}
      </td>
      <td className={td}>
        {bien.ville || <span className="text-text-muted">—</span>}
      </td>
      <td className={td}>
        {dispo === "DISPONIBLE" || dispo === "EN PRÉPARATION" ? (
          <span
            className={cn(
              "text-[10px] font-medium px-2 py-0.5 rounded-full",
              dispo === "DISPONIBLE" ? "bg-success/10 text-success" : "bg-warning/10 text-warning",
            )}
          >
            {dispoLabel}
          </span>
        ) : dispo === "LOUÉ" || dispo === "VENDU" ? (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-text-muted/10 text-text-muted">
            {dispoLabel}
          </span>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
      <td className={cn(td, "text-center")}>
        <QualityBadge value={bien.photosOk || ""} emptyLabel={t("biens.photosOptions.pas_de_photo")} />
      </td>
      <td className={cn(td, "text-center")}>
        <QualityBadge value={bien.videoOk || ""} emptyLabel={t("biens.videoOptions.pas_de_video")} />
      </td>
      <td className={cn(td, "text-center")}>
        <OkBadge ok={bien.textesOk === true} />
      </td>
      <td className={td}>
        {bien.alerteTexte ? (
          <span
            className={cn(
              "inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded",
              shouldModifyText(bien.alerteTexte)
                ? "bg-danger/10 text-danger"
                : "bg-surface-2 text-text-secondary",
            )}
          >
            {formatAlerteDate(bien.alerteTexte)}
          </span>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
      <td className={td}>
        {bien.statutPublication || <span className="text-text-muted">—</span>}
      </td>
      <td className={cn(td, "text-center")}>
        <PortalsCellMini bien={bien as unknown as Bien} />
      </td>
    </tr>
  );
}
